import * as angular from 'angular';
import * as _ from 'lodash';
import {States} from "../../shared/config/constant.module";
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import EditFormulaName, {FormulaDetail, openEditFormula} from "./editFormula.controller";
import template from './formula.tpl.html';
import {Plant} from "../../models/navigation";
import LocationsServiceName, {LocationsService, PlantLocation} from "../../services/locations/locations.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import DataPointServiceName, {DataPointService} from "../../services/dataPoints/dataPoints.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import UIUtilityServiceName, {UIUtilityService} from "../../services/utility/uiUtility.service";
import NotifyServiceName, {NotifyService} from "../../services/notify/notify.service";

let requires = [EditFormulaName, LocationsServiceName, LoaderServiceName, NavigationServiceName, DataPointServiceName,
  AuthDataServiceName, UIUtilityServiceName, NotifyServiceName];

interface FormulaController {
  allDataPoints: FormulaDetail[];
  formulas: FormulaDetail[];
  plants: Plant[];
  selectedPlant: Plant;
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  plantChanged(): void;
  add(): void;
  edit(instance: apsViewModels.DataPointDetail): void;
  remove(instance: apsViewModels.DataPointDetail): void;
  getTypeName(type: apsEnums.DataPointType): string;
  locationChanged(): void;
}

class FormulaCtrl implements FormulaController {
  allDataPoints: FormulaDetail[];
  formulas: FormulaDetail[];
  plants: Plant[];
  selectedPlant: Plant;
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  private loaderService: LoaderService;
  private navigationService: NavigationService;
  private dataPointService: DataPointService;
  private authDataService: AuthDataService;
  private uiUtilityService: UIUtilityService;
  private notifyService: NotifyService;
  private $modal: mm.foundation.modal;
  private locationsService: LocationsService;
  private plantLocations: apsViewModels.LocationNavigation[];

  private locationPromise: angular.IPromise<any>;

  constructor(
    loaderService: LoaderService,
    dataPointService: DataPointService,
    navigationService: NavigationService,
    authDataService: AuthDataService,
    uiUtilityService: UIUtilityService,
    notifyService: NotifyService,
    locationsService: LocationsService,
    $modal: mm.foundation.modal
  ) {
    this.loaderService = loaderService;
    this.navigationService = navigationService;
    this.dataPointService = dataPointService;
    this.authDataService = authDataService;
    this.uiUtilityService = uiUtilityService;
    this.notifyService = notifyService;
    this.locationsService = locationsService;
    this.$modal = $modal;

    let promise = this.navigationService.getNavigation().then(nav => {
      let plants = nav.plants();
      this.plants = _.filter(plants, (plant: Plant) => {
        return authDataService.isPlantAdmin(plant.id) || authDataService.isAPSIOAdminForPlant(plant.id);
      });

      if (nav.selectedPlant && _.includes(this.plants, nav.selectedPlant)) {
        this.selectedPlant = nav.selectedPlant;
      } else {
        this.selectedPlant = _.first(this.plants);
      }

      this.locationPromise = this.navigationService.getLocations(false).then(locations => {
        this.plantLocations = locations;
        this.loadLocations();
      });

      return this.loadDataPoints();
    });

    this.loaderService.loadingPromise(promise);
  }

  private loadLocations(): void {
    this.locations = this.locationsService
      .getPlantLocations(this.selectedPlant, this.plantLocations);
    this.selectedLocation = null;
  }

  locationChanged(): void {
    this.filterFormulas();
  }

  private setFormulaDetail(dp: FormulaDetail) {
    dp.location = _.find(this.locations, l => l.location.id === dp.plantLocationId).name;
    dp.typeName = this.getTypeName(dp.type);
  }

  private loadDataPoints(): angular.IPromise<any> {
    return this.dataPointService
      .get(this.selectedPlant.id)
      .then(dataPoints => {
        return this.locationPromise.then(() => {
          this.allDataPoints = <FormulaDetail[]>dataPoints;
          this.allDataPoints.forEach(dp => this.setFormulaDetail(dp));
          this.filterFormulas();
        });
      });
  }

  private filterFormulas(): void {
    this.formulas = _.filter(this.allDataPoints, dp =>
      dp.configType === apsEnums.DataPointConfigType.Formula);
    if (this.selectedLocation) {
      this.formulas = _.filter(this.formulas, {plantLocationId: this.selectedLocation.location.id});
    }
  }

  plantChanged(): void {
    this.loadLocations();
    this.loaderService.loadingPromise(this.loadDataPoints());
  }

  add(): void {
    this.edit(null);
  }

  edit(instance: apsViewModels.DataPointDetail): void {
    let result = openEditFormula({
      plant: this.selectedPlant,
      allDataPoints: this.allDataPoints,
      instance: instance,
      locations: this.locations,
      selectedLocation: this.selectedLocation
    }, this.$modal);

    result.then(newInstance => {
      if (!newInstance) {
        return;
      }

      this.setFormulaDetail(newInstance);
      if (instance == null) {
        this.allDataPoints.push(newInstance);
      } else {
        let index = _.indexOf(this.allDataPoints, instance);
        this.allDataPoints[index] = newInstance;
      }
      this.filterFormulas();
    });
  }

  remove(instance: apsViewModels.DataPointDetail): void {
    this.uiUtilityService.confirm('Are you sure you want to delete this formula?').then(confirmed => {
      if (!confirmed) {
        return;
      }

      this.loaderService.loadingPromise(this.dataPointService.remove({
        id: instance.id,
        plantId: this.selectedPlant.id,
        plantLocationId: instance.plantLocationId
      }).then(() => {
        _.remove(this.allDataPoints, instance);
        this.filterFormulas();
      }, this.notifyService.handleApiErrors));
    });
  }

  getTypeName(type: apsEnums.DataPointType): string {
    return apsEnums.DataPointType[type];
  }

}

export default angular.module('aps.controller.formula', requires)
  .config(($stateProvider: angular.ui.IStateProvider) => {
    $stateProvider.state({
      name: States.FORMULAS,
      url: '/admin/formulas',
      controller: 'FormulaCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Formulas',
        authorizeAPSIOAdmin: true
      }
    });
  })
  .controller('FormulaCtrl', FormulaCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/formulas/formula.controller.ts